import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, ChevronRight, Crown, User } from 'lucide-react'
import TeamAvatar from '../../../team/components/TeamAvatar'
import AnimatedNumber from '../../../../components/AnimatedNumber'
import { CrystalAmount, ProgressBar, PumpkinIcon } from './shared'

/**
 * Банда в эвенте: тыквы (= билеты), место, условие по участникам и следующая ступень лестницы.
 * Общий блок для своей банды на странице эвента и для карточки любой банды.
 */
const TeamProgressCard = ({ team, title, onOpen, actionLabel, badge = null }) => {
  const { t } = useTranslation()
  if (!team) return null
  const needed = Math.max(0, Number(team.contributors_needed) || 0)
  const step = team.next_step

  return (
    <section className="hev-card relative overflow-hidden rounded-2xl p-5">
      <div
        className="pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full bg-orange-500/15 blur-3xl"
        aria-hidden="true"
      />

      {title && (
        <p className="relative text-[11px] font-bold uppercase tracking-wide text-orange-300">{title}</p>
      )}

      <div className={`relative flex items-center gap-3 ${title ? 'mt-3' : ''}`}>
        <TeamAvatar team={team} size={52} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-black text-white">{team.name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-400/30 bg-yellow-400/10 px-2 py-0.5 text-[11px] font-bold text-yellow-200">
              <Crown size={11} />
              {team.place ? t('halloweenEvent.team.place', { place: team.place }) : t('halloweenEvent.team.noPlace')}
            </span>
            {badge}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="flex items-center justify-end gap-1.5">
            <PumpkinIcon size={22} glow />
            <span className="hev-flame-text text-3xl font-black leading-none tabular-nums">
              <AnimatedNumber value={team.pumpkins} />
            </span>
          </p>
          <p className="mt-1 text-[11px] text-text-secondary">{t('halloweenEvent.team.tickets')}</p>
        </div>
      </div>

      {/* Условие лестницы: тыквы принесли хотя бы N разных участников */}
      <div className="relative mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5">
        <div className="flex shrink-0 items-center -space-x-1.5">
          {Array.from({ length: needed }, (_, index) => (
            <span
              key={index}
              className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#120d18] ${index < team.contributors ? 'bg-accent-primary text-dark-primary' : 'bg-white/10 text-text-secondary'}`}
            >
              <User size={13} />
            </span>
          ))}
        </div>
        <p className={`min-w-0 text-xs ${team.contributors_done ? 'text-accent-primary' : 'text-text-secondary'}`}>
          {team.contributors_done
            ? t('halloweenEvent.team.contributorsDone', { count: team.contributors })
            : t('halloweenEvent.team.contributors', { count: team.contributors, needed })}
        </p>
      </div>

      {/* Следующая ступень лестницы: полоска только у недостигнутой */}
      {step ? (
        <div className="relative mt-3 rounded-xl border border-orange-400/20 bg-orange-500/[0.06] px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-white">
              {t('halloweenEvent.team.nextStep', { step: step.step, count: step.pumpkins })}
            </p>
            <CrystalAmount value={step.reward} className="text-sm font-bold text-accent-primary" />
          </div>
          {step.pumpkins_done ? (
            <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-amber-200">
              <AlertCircle size={14} className="mt-px shrink-0 text-amber-300" />
              {t('halloweenEvent.team.needContributor')}
            </p>
          ) : (
            <div className="mt-2.5 flex items-center gap-2">
              <ProgressBar pct={step.pct} className="flex-1" />
              <span className="w-9 shrink-0 text-right text-xs font-bold tabular-nums text-orange-200">{step.pct ?? 0}%</span>
            </div>
          )}
        </div>
      ) : (
        <div className="relative mt-3 flex items-center gap-2 rounded-xl border border-accent-primary/25 bg-accent-primary/10 px-3 py-2.5">
          <CheckCircle2 size={16} className="shrink-0 text-accent-primary" />
          <p className="text-sm font-semibold text-white">{t('halloweenEvent.team.allSteps')}</p>
        </div>
      )}

      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="relative mt-3 flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-semibold text-white transition-all hover:bg-white/10 active:scale-[0.98]"
        >
          {actionLabel}
          <ChevronRight size={16} className="text-orange-300 rtl:rotate-180" />
        </button>
      )}
    </section>
  )
}

export default TeamProgressCard
